import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig, prefersReducedMotion } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/**
 * MoonCommerce cart extensions for the native Horizon cart drawer and cart page.
 *
 * - <mc-cart-club>: slim Really Good Club row (add / remove the membership line, Toki points for members).
 * - <mc-cart-upsell>: "Complete your order" row fed by Section Rendering of sections/mc-cart-upsell.
 * - Bridge: custom buy boxes that call /cart/add.js themselves and then dispatch
 *   `cart:refresh` / `cart:updated` get the bubble, cart drawer and cart page refreshed,
 *   and the drawer opens when the item count went up.
 * - Plain product forms (form action /cart/add outside product-form-component) are
 *   submitted with fetch so they open the drawer instead of reloading to /cart.
 * - Screen reader announcements after cart changes.
 */

/** @returns {{ strings: Record<string, string> }} */
function getConfig() {
  const element = document.getElementById('mc-cart-config');
  try {
    return JSON.parse(element?.textContent || '{}');
  } catch {
    return { strings: {} };
  }
}

const config = getConfig();
const strings = config.strings || {};

/** @returns {string[]} Section ids of every cart-items-component on the page. */
function cartSectionIds() {
  const ids = new Set();
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  }
  return Array.from(ids);
}

/**
 * Reads the item count from a rendered cart section.
 * @param {Record<string, string> | undefined} sections
 * @returns {number | null}
 */
function itemCountFromSections(sections) {
  if (!sections) return null;
  for (const html of Object.values(sections)) {
    if (!html) continue;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const count = doc.querySelector('[ref="cartItemCount"]')?.textContent;
    if (count !== undefined && count !== null) return parseInt(count, 10) || 0;
    if (doc.querySelector('cart-items-component')) return 0;
  }
  return null;
}

/** @returns {number} Item count currently shown in the header bubble. */
function currentBubbleCount() {
  const count = document.querySelector('[ref="cartBubbleCount"]')?.textContent?.trim();
  return count ? parseInt(count, 10) || 0 : 0;
}

/* ---------------------------------------------------------------------------
 * Announcer
 * ------------------------------------------------------------------------- */

let announceTimer = 0;

/** @returns {HTMLElement} A polite live region inside the open cart dialog, or the body. */
function getAnnouncer() {
  const host = document.querySelector('.cart-drawer__dialog[open]') || document.body;
  let region = host.querySelector(':scope > [data-mc-cart-announcer]');
  if (!(region instanceof HTMLElement)) {
    region = document.createElement('div');
    region.className = 'visually-hidden';
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    region.dataset.mcCartAnnouncer = '';
    host.appendChild(region);
  }
  return /** @type {HTMLElement} */ (region);
}

/** @returns {Element | null} */
function cartScope() {
  return document.querySelector('.cart-drawer__dialog[open]') || document.querySelector('cart-items-component');
}

/** @returns {string} Item count and total currently rendered, used to detect when the morph landed. */
function cartSignature() {
  const scope = cartScope();
  if (!scope) return '';
  const count = scope.querySelector('[ref="cartItemCount"]')?.textContent?.trim() || '0';
  const total = scope.querySelector('[data-testid="cart-total-value"]')?.textContent?.trim() || '';
  return `${count}|${total}`;
}

let lastAnnounced = '';

function announceCart() {
  clearTimeout(announceTimer);
  const region = getAnnouncer();
  const before = lastAnnounced;
  const started = Date.now();

  const speak = () => {
    const scope = cartScope();
    if (!scope) return;
    const countText = scope.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
    let message = '';
    if (!countText) {
      message = strings.empty || '';
    } else {
      const total = scope.querySelector('[data-testid="cart-total-value"]')?.textContent?.trim() || '';
      message = (strings.updated || '').replace('[count]', countText).replace('[total]', total);
      const progress = scope.querySelector('[data-mc-cart-progress-message]')?.textContent?.trim();
      if (progress) message += ` ${progress}`;
    }
    lastAnnounced = cartSignature();
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 50);
  };

  // Wait for Section Rendering to morph the new cart markup in (or give up after 4 seconds).
  const poll = () => {
    if (cartSignature() !== before || Date.now() - started > 4000) {
      announceTimer = window.setTimeout(speak, 150);
      return;
    }
    announceTimer = window.setTimeout(poll, 150);
  };
  announceTimer = window.setTimeout(poll, 150);
}

// Baseline so the first change is detected against what was rendered on page load.
lastAnnounced = cartSignature();
document.addEventListener(ThemeEvents.cartUpdate, () => announceCart());

/* ---------------------------------------------------------------------------
 * Really Good Club row (add / remove the membership line, "Why join?")
 * ------------------------------------------------------------------------- */

class McCartClub extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    if (this.querySelector('[data-mc-toki]')) loadToki();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    if (target.closest?.('[data-mc-club-toggle]')) {
      setClubOpen(!document.documentElement.classList.contains('mc-club-open'));
      return;
    }
    const why = target.closest?.('[data-mc-club-why]');
    if (why instanceof HTMLButtonElement) {
      const panel = document.getElementById(why.getAttribute('aria-controls') || '');
      const open = why.getAttribute('aria-expanded') !== 'true';
      why.setAttribute('aria-expanded', String(open));
      if (panel) panel.hidden = !open;
      return;
    }
    if (target.closest?.('[data-mc-club-add]')) this.#update(true);
    else if (target.closest?.('[data-mc-club-remove]')) this.#update(false);
  };

  /** @param {boolean} adding */
  async #update(adding) {
    if (this.#busy) return;
    this.#busy = true;
    this.classList.add('mc-club--busy');
    this.setAttribute('aria-busy', 'true');
    this.#setError('');

    const sections = cartSectionIds();
    const lineKey = this.dataset.lineKey;

    try {
      let response;
      if (adding) {
        response = await fetch(`${Theme.routes.cart_add_url}`, {
          ...fetchConfig('json', {
            body: JSON.stringify({
              items: [
                {
                  id: Number(this.dataset.variantId),
                  quantity: 1,
                  selling_plan: Number(this.dataset.sellingPlanId),
                },
              ],
              sections: sections.join(','),
              sections_url: window.location.pathname,
            }),
          }),
        });
      } else {
        if (!lineKey) throw new Error('Membership line key missing');
        response = await fetch(`${Theme.routes.cart_change_url}`, {
          ...fetchConfig('json', {
            body: JSON.stringify({
              id: lineKey,
              quantity: 0,
              sections: sections.join(','),
              sections_url: window.location.pathname,
            }),
          }),
        });
      }

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status || data.errors) {
        throw new Error(data.description || data.message || data.errors || 'Cart request failed');
      }

      const itemCount = typeof data.item_count === 'number' ? data.item_count : itemCountFromSections(data.sections);

      document.dispatchEvent(
        new CartUpdateEvent(adding ? {} : data, 'mc-cart-club', {
          source: 'mc-cart-club',
          itemCount: itemCount ?? currentBubbleCount(),
          sections: data.sections,
          // @ts-ignore custom flag read by cart-drawer.js
          skipOpen: true,
        })
      );
      refocusAfterMorph('[data-mc-club-remove], [data-mc-club-add]');
    } catch (error) {
      console.error(error);
      this.#setError(this.dataset.errorText || '');
    } finally {
      this.#busy = false;
      this.classList.remove('mc-club--busy');
      this.removeAttribute('aria-busy');
    }
  }

  /** @param {string} message */
  #setError(message) {
    const element = /** @type {HTMLElement | null} */ (this.querySelector('[data-mc-club-error]'));
    if (!element) return;
    element.textContent = message;
    element.hidden = !message;
  }
}

if (!customElements.get('mc-cart-club')) customElements.define('mc-cart-club', McCartClub);

/* ---------------------------------------------------------------------------
 * Club card: one compact row, collapsed on every page view; the row expands the details.
 * The open state lives on <html> so cart re-renders keep it.
 * ------------------------------------------------------------------------- */

function syncClubToggles() {
  const open = document.documentElement.classList.contains('mc-club-open');
  for (const toggle of document.querySelectorAll('[data-mc-club-toggle]')) {
    if (toggle.getAttribute('aria-expanded') !== String(open)) toggle.setAttribute('aria-expanded', String(open));
  }
}

/** @param {boolean} open */
function setClubOpen(open) {
  document.documentElement.classList.toggle('mc-club-open', open);
  syncClubToggles();
  if (open) window.setTimeout(scanCounts, 80);
}

/* ---------------------------------------------------------------------------
 * Animated figures: [data-mc-count] elements carry the raw value (minor units) in
 * data-mc-value. They count up the first time they are seen, and animate from the
 * old to the new value whenever a cart re-render changes data-mc-value.
 * ------------------------------------------------------------------------- */

/** @type {Map<string, number>} */
const countValues = new Map();
/** @type {WeakMap<Element, number>} */
const countFrames = new WeakMap();
const NUMBER_PATTERN = /\d[\d,]*(?:\.\d+)?/;

/**
 * @param {HTMLElement} element
 * @param {number} from
 * @param {number} to
 */
function animateCount(element, from, to) {
  const template = element.textContent || '';
  const match = template.match(NUMBER_PATTERN);
  if (!match || prefersReducedMotion() || from === to) return;
  const decimals = (match[0].split('.')[1] || '').length;
  const format = new Intl.NumberFormat('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  const scale = decimals ? 100 : 1;
  const start = performance.now();
  const duration = 750;
  cancelAnimationFrame(countFrames.get(element) || 0);
  const frame = (now) => {
    const t = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - t, 3);
    const value = (from + (to - from) * eased) / scale;
    element.textContent = template.replace(NUMBER_PATTERN, format.format(value));
    if (t < 1) countFrames.set(element, requestAnimationFrame(frame));
    else element.textContent = template;
  };
  countFrames.set(element, requestAnimationFrame(frame));
}

function scanCounts() {
  for (const element of document.querySelectorAll('[data-mc-count]')) {
    if (!(element instanceof HTMLElement) || !element.getClientRects().length) continue;
    // Figures inside the collapsed Club details count up when the row is opened.
    if (element.closest('[data-mc-club-panel]') && !document.documentElement.classList.contains('mc-club-open')) continue;
    const key = element.dataset.mcCount || '';
    const value = Number(element.dataset.mcValue);
    if (!key || !Number.isFinite(value)) continue;
    const previous = countValues.get(key);
    countValues.set(key, value);
    if (previous === undefined) {
      if (element.dataset.mcCountIntro !== 'false') animateCount(element, 0, value);
    } else if (previous !== value) {
      animateCount(element, previous, value);
    }
  }
}

let countScheduled = 0;
new MutationObserver((mutations) => {
  let relevant = false;
  for (const mutation of mutations) {
    if (mutation.type === 'attributes') {
      relevant = true;
      break;
    }
    if (/** @type {Element} */ (mutation.target).closest?.('cart-items-component, .cart-drawer')) {
      relevant = true;
      break;
    }
  }
  if (!relevant) return;
  cancelAnimationFrame(countScheduled);
  countScheduled = requestAnimationFrame(() => {
    syncClubToggles();
    scanCounts();
  });
}).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-mc-value', 'open'] });

syncClubToggles();

/**
 * After a cart re-render the clicked button may be gone; keep keyboard focus inside the cart.
 * @param {string} selector
 */
function refocusAfterMorph(selector) {
  window.setTimeout(() => {
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) return;
    const scope = cartScope();
    const next = /** @type {HTMLElement | null} */ (
      scope?.querySelector(selector) || scope?.querySelector('#checkout') || null
    );
    next?.focus({ preventScroll: true });
  }, 250);
}

/* ---------------------------------------------------------------------------
 * Toki points for logged-in members (only when assets/mc-toki.js is present)
 * ------------------------------------------------------------------------- */

let tokiRequested = false;

function loadToki() {
  if (tokiRequested) return;
  const api = /** @type {any} */ (window).MCToki;
  if (!api || typeof api.getBalance !== 'function') {
    // mc-toki.js (loaded by the utility bar) may arrive after this module.
    document.addEventListener('mc:toki:balance', () => loadToki(), { once: true });
    window.setTimeout(() => {
      const late = /** @type {any} */ (window).MCToki;
      if (!tokiRequested && late && typeof late.getBalance === 'function') loadToki();
    }, 2500);
    return;
  }
  tokiRequested = true;
  const timeout = new Promise((resolve) => window.setTimeout(() => resolve(null), 6000));
  Promise.race([Promise.resolve().then(() => api.getBalance()), timeout])
    .then((result) => {
      // getBalance() resolves { points, name, value } (value = store credit, e.g. 11.78 or "£11.78") or null.
      const points = typeof result === 'number' ? result : Number(/** @type {any} */ (result)?.points);
      if (!Number.isFinite(points) || points <= 0) return;
      const raw = /** @type {any} */ (result)?.value;
      let value = '';
      if (typeof raw === 'string' && raw.trim()) value = raw.trim();
      else if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
        value =
          typeof api.formatMoney === 'function'
            ? api.formatMoney(raw)
            : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(raw);
      }
      const template = value ? strings.tokiPoints || '' : strings.tokiPointsPlain || '';
      const text = template.replace('[points]', points.toLocaleString('en-GB')).replace('[value]', value);
      if (!text) return;
      // Stored on <html> so cart re-renders (morphs) keep showing it.
      const root = document.documentElement;
      root.style.setProperty('--mc-toki-points', JSON.stringify(text));
      root.classList.add('mc-has-toki');
    })
    .catch(() => {});
}

/* ---------------------------------------------------------------------------
 * "Complete your order" upsell row
 * ------------------------------------------------------------------------- */

/** @type {Map<string, string>} */
const upsellCache = new Map();

/**
 * @param {string} url
 * @returns {Promise<{ html: string, count: number }>}
 */
async function fetchUpsell(url) {
  let html = upsellCache.get(url);
  if (html === undefined) {
    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      html = response.ok ? await response.text() : '';
    } catch {
      html = '';
    }
    upsellCache.set(url, html);
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const inner = doc.querySelector('.mc-upsell__inner');
  return { html: inner ? inner.outerHTML : '', count: Number(inner?.getAttribute('data-count') || 0) };
}

class McCartUpsell extends HTMLElement {
  static get observedAttributes() {
    return ['data-signature'];
  }

  #timer = 0;
  #request = 0;
  #refocus = false;
  #pending = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#schedule(0);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    clearTimeout(this.#timer);
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue !== null && oldValue !== newValue && this.isConnected) this.#schedule(120);
  }

  get slot() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-mc-upsell-slot]'));
  }

  /** @param {number} delay */
  #schedule(delay) {
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#load(), delay);
  }

  /** The latest cart line (the server renders "Pairs well with ..." for the same line). */
  #pickAnchor() {
    return (this.dataset.productIds || '').split(',').filter(Boolean)[0] || '';
  }

  /** True when this host is laid out (the drawer has an inline and a side-panel host; only one shows). */
  #isShown() {
    /** @type {Element | null} */
    let element = this;
    while (element && element.tagName !== 'DIALOG' && element !== document.body) {
      if (getComputedStyle(element).display === 'none') return false;
      element = element.parentElement;
    }
    return true;
  }

  wake() {
    if (this.#pending) this.#schedule(0);
  }

  async #load() {
    const slot = this.slot;
    if (!slot) return;
    if (!this.#isShown()) {
      this.#pending = true;
      return;
    }
    this.#pending = false;
    const request = ++this.#request;
    const section = this.dataset.section || 'mc-cart-upsell';
    const signature = encodeURIComponent(this.dataset.signature || '');
    const anchor = this.#pickAnchor();
    const recsUrl = this.dataset.recommendationsUrl || '/recommendations/products';
    const rootUrl = this.dataset.rootUrl || '/';

    /** @type {{ html: string, count: number } | null} */
    let best = null;
    if (anchor) {
      // Search & Discovery complementary products are not set up on this store, so only "related" is asked for.
      best = await fetchUpsell(`${recsUrl}?product_id=${anchor}&limit=10&intent=related&section_id=${section}&v=${signature}`);
    }
    if (!best || !best.count) {
      const separator = rootUrl.includes('?') ? '&' : '?';
      best = await fetchUpsell(`${rootUrl}${separator}section_id=${section}&v=${signature}`);
    }
    if (request !== this.#request) return;

    const hadCards = !!slot.querySelector('[data-mc-upsell-card]');
    slot.removeAttribute('aria-busy');
    if (!best || !best.count) {
      slot.innerHTML = '';
      slot.dataset.empty = '';
      if (this.#refocus) {
        this.#refocus = false;
        refocusAfterMorph('#checkout');
      }
      return;
    }
    delete slot.dataset.empty;
    slot.innerHTML = best.html;
    slot.classList.toggle('mc-upsell__slot--fresh', !hadCards);
    this.#updateNav();
    slot.querySelector('[data-mc-upsell-track]')?.addEventListener('scroll', () => this.#updateNav(), { passive: true });
    if (this.#refocus) {
      this.#refocus = false;
      const active = document.activeElement;
      if (!active || active === document.body || !active.isConnected || this.contains(active)) {
        /** @type {HTMLElement | null} */ (slot.querySelector('[data-mc-upsell-add]'))?.focus({ preventScroll: true });
      }
    }
  }

  #updateNav() {
    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-mc-upsell-track]'));
    const prev = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-mc-upsell-prev]'));
    const next = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-mc-upsell-next]'));
    if (!track || !prev || !next) return;
    const max = track.scrollWidth - track.clientWidth;
    const nav = /** @type {HTMLElement | null} */ (this.querySelector('[data-mc-upsell-nav]'));
    if (nav) nav.hidden = max <= 4;
    prev.disabled = track.scrollLeft <= 4;
    next.disabled = track.scrollLeft >= max - 4;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    const track = /** @type {HTMLElement | null} */ (this.querySelector('[data-mc-upsell-track]'));
    if (track && target.closest?.('[data-mc-upsell-prev], [data-mc-upsell-next]')) {
      const direction = target.closest('[data-mc-upsell-next]') ? 1 : -1;
      track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      return;
    }
    const button = target.closest?.('[data-mc-upsell-add]');
    if (button instanceof HTMLButtonElement) this.#add(button);
  };

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    if (button.disabled) return;
    const card = /** @type {HTMLElement | null} */ (button.closest('[data-mc-upsell-card]'));
    const error = /** @type {HTMLElement | null} */ (card?.querySelector('.mc-upsell-card__error'));
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    card?.classList.add('mc-upsell-card--busy');
    if (error) error.hidden = true;

    // Upsell tracking: hidden line-item properties (a leading underscore keeps them out of the
    // cart and checkout, but they stay on the order line for reporting and exports).
    const upsellKinds = {
      gap: 'Free delivery gap',
      pairs: 'Pairs with',
      addon: 'Add-on',
      star: 'Star Bottle',
      gift: 'Gift',
      best: 'Best sellers',
    };
    const kind = card?.dataset.mcUpsellKind || '';
    const properties = {
      _upsell: this.closest('.cart-drawer') ? 'Cart drawer' : 'Cart page',
      _upsell_type: upsellKinds[kind] || kind || 'Upsell',
      _upsell_spot: card?.dataset.mcUpsellLayout === 'hero' ? 'Top pick' : 'Carousel',
    };
    const label = (card?.dataset.mcUpsellLabel || '').trim();
    if (label) properties._upsell_label = label.slice(0, 80);

    try {
      const response = await fetch(`${Theme.routes.cart_add_url}`, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            items: [{ id: Number(button.dataset.variantId), quantity: 1, properties }],
            sections: cartSectionIds().join(','),
            sections_url: window.location.pathname,
          }),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add failed');

      const lineKey = data.items?.[0]?.key || '';
      const title = data.items?.[0]?.product_title || '';

      card?.classList.remove('mc-upsell-card--busy');
      card?.classList.add('mc-upsell-card--added');
      const text = button.querySelector('.mc-upsell-card__add-text');
      if (text instanceof HTMLElement) text.textContent = text.dataset.addedText || text.textContent;

      const itemCount = itemCountFromSections(data.sections);
      document.dispatchEvent(
        new CartAddEvent({}, 'mc-cart-upsell', {
          source: 'mc-cart-upsell',
          itemCount: itemCount ?? currentBubbleCount() + 1,
          sections: data.sections,
          // @ts-ignore custom flag read by cart-drawer.js
          skipOpen: true,
        })
      );
      highlightLine(lineKey);
      try {
        // Custom event for analytics pixels (Shopify Customer events).
        /** @type {any} */ (window).Shopify?.analytics?.publish?.('mc_upsell_added', {
          variantId: Number(button.dataset.variantId),
          productTitle: title,
          ...properties,
        });
      } catch (publishError) {}
      if (title) getAnnouncer().textContent = (strings.upsellAdded || '').replace('[title]', title);
      this.#refocus = true;
      button.focus({ preventScroll: true });
    } catch (err) {
      console.error(err);
      button.disabled = false;
      card?.classList.remove('mc-upsell-card--busy');
      if (error) {
        error.textContent = strings.upsellError || strings.addError || '';
        error.hidden = false;
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('mc-cart-upsell')) customElements.define('mc-cart-upsell', McCartUpsell);

// The drawer swaps between the side panel and the inline row at 1100px; load whichever becomes visible.
window.matchMedia('(min-width: 1100px)').addEventListener('change', () => {
  for (const element of document.querySelectorAll('mc-cart-upsell')) /** @type {any} */ (element).wake?.();
});

/**
 * Plays the "arrived" animation on a freshly added cart line once the morph has put it in place.
 * @param {string} key
 */
function highlightLine(key) {
  if (!key) return;
  const started = Date.now();
  const find = () => {
    const rows = document.querySelectorAll(`.cart-items__table-row[data-key="${CSS.escape(key)}"]`);
    if (rows.length) {
      for (const row of rows) {
        row.classList.remove('mc-cart-row--new');
        void (/** @type {HTMLElement} */ (row).offsetWidth);
        row.classList.add('mc-cart-row--new');
        if (!prefersReducedMotion() && row.closest('.cart-drawer__dialog')) {
          row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
      return;
    }
    if (Date.now() - started < 4000) window.setTimeout(find, 80);
  };
  window.setTimeout(find, 30);
}

/* ---------------------------------------------------------------------------
 * Bridge for custom buy boxes that add to the cart themselves
 * ------------------------------------------------------------------------- */

let bridgeTimer = 0;
let bridgeKnownCount = currentBubbleCount();

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const count = /** @type {any} */ (event).detail?.data?.itemCount;
  const source = /** @type {any} */ (event).detail?.data?.source;
  // Product form counts are deltas; read the bubble once it has updated.
  if (source === 'product-form-component') {
    window.setTimeout(() => (bridgeKnownCount = currentBubbleCount()), 0);
  } else if (typeof count === 'number') {
    bridgeKnownCount = count;
  }
});

async function refreshFromBridge() {
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    const grew = cart.item_count > bridgeKnownCount;
    bridgeKnownCount = cart.item_count;
    document.dispatchEvent(
      new CartAddEvent(cart, 'mc-cart-bridge', {
        source: 'mc-cart-bridge',
        itemCount: cart.item_count,
        // @ts-ignore custom flag read by cart-drawer.js
        skipOpen: !grew,
      })
    );
  } catch (error) {
    console.error(error);
  }
}

for (const name of ['cart:refresh', 'cart:updated']) {
  document.addEventListener(name, () => {
    clearTimeout(bridgeTimer);
    bridgeTimer = window.setTimeout(refreshFromBridge, 60);
  });
}

/* ---------------------------------------------------------------------------
 * Plain product forms (outside product-form-component)
 * ------------------------------------------------------------------------- */

/** @param {HTMLFormElement} form */
function isPlainAddForm(form) {
  if (form.closest('product-form-component')) return false;
  if (form.hasAttribute('data-mc-native-submit')) return false;
  const action = form.getAttribute('action') || '';
  try {
    return /\/cart\/add\/?$/.test(new URL(action, window.location.href).pathname);
  } catch {
    return false;
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {string} message
 */
function showFormError(form, message) {
  let element = form.nextElementSibling;
  if (!(element instanceof HTMLElement) || !element.matches('[data-mc-cart-form-error]')) {
    element = document.createElement('p');
    element.className = 'mc-cart-form-error';
    element.setAttribute('role', 'alert');
    /** @type {HTMLElement} */ (element).dataset.mcCartFormError = '';
    form.insertAdjacentElement('afterend', element);
  }
  /** @type {HTMLElement} */ (element).textContent = message;
  /** @type {HTMLElement} */ (element).hidden = !message;
}

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || event.defaultPrevented || !isPlainAddForm(form)) return;
  // Without a drawer (cart page, or drawer disabled) keep the native submit.
  if (!document.querySelector('cart-drawer-component') && !document.querySelector('cart-items-component')) return;

  event.preventDefault();
  const submitter = /** @type {HTMLButtonElement | null} */ (
    /** @type {SubmitEvent} */ (event).submitter || form.querySelector('[type="submit"]')
  );
  if (submitter?.disabled) return;
  submitter?.setAttribute('aria-busy', 'true');
  if (submitter) submitter.disabled = true;
  showFormError(form, '');

  const formData = new FormData(form);
  const sections = cartSectionIds();
  if (sections.length) {
    formData.append('sections', sections.join(','));
    formData.append('sections_url', window.location.pathname);
  }

  try {
    const response = await fetch(`${Theme.routes.cart_add_url}`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      showFormError(form, data.description || data.message || strings.addError || '');
      return;
    }
    const itemCount = itemCountFromSections(data.sections);
    document.dispatchEvent(
      new CartAddEvent({}, 'mc-cart-form', {
        source: 'mc-cart-form',
        itemCount: itemCount ?? currentBubbleCount() + (Number(formData.get('quantity')) || 1),
        sections: data.sections,
      })
    );
  } catch (error) {
    console.error(error);
    showFormError(form, strings.addError || '');
  } finally {
    submitter?.removeAttribute('aria-busy');
    if (submitter) submitter.disabled = false;
  }
});

/* ---------------------------------------------------------------------------
 * Safety net: never leave the drawer on a stale "empty" state
 * ------------------------------------------------------------------------- */

let staleTimer = 0;

/**
 * If the cart has items but a cart section still shows the empty state (for example an add from a
 * product form whose response did not include the cart sections, and the GET re-render failed),
 * re-render the cart sections through POST /cart/update.js, which always returns them.
 */
async function repairStaleCart() {
  const stale = Array.from(document.querySelectorAll('cart-items-component')).filter(
    (element) => element instanceof HTMLElement && element.dataset.sectionId && !element.querySelector('.cart-form')
  );
  if (!stale.length) return;
  try {
    const ids = stale.map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId).join(',');
    const response = await fetch(`${Theme.routes.cart_update_url}`, {
      ...fetchConfig('json', { body: JSON.stringify({ updates: {}, sections: ids, sections_url: window.location.pathname }) }),
    });
    const data = await response.json();
    if (!data || !data.item_count || !data.sections) return;
    for (const element of stale) {
      const id = /** @type {HTMLElement} */ (element).dataset.sectionId || '';
      if (data.sections[id] && !element.querySelector('.cart-form')) {
        morphSection(id, data.sections[id], element.hasAttribute('data-drawer') ? 'hydration' : 'full');
      }
    }
  } catch (error) {
    console.error(error);
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const count = /** @type {any} */ (event).detail?.data?.itemCount;
  if (typeof count === 'number' && count === 0) return;
  clearTimeout(staleTimer);
  staleTimer = window.setTimeout(() => {
    if (currentBubbleCount() > 0) repairStaleCart();
  }, 900);
});

/* ---------------------------------------------------------------------------
 * International shipping tiers. The automatic discount works on GBP thresholds,
 * so the bar converts them into the shopper's currency with Shopify.currency.rate.
 * ------------------------------------------------------------------------- */

class McCartProgress extends HTMLElement {
  static get observedAttributes() {
    return ['data-total'];
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.render();
  }

  /** @param {number} minor Amount in the cart currency's minor units */
  #money(minor) {
    const template = this.dataset.money || '';
    const match = template.match(NUMBER_PATTERN);
    const value = minor / 100;
    if (!match) return value.toFixed(2);
    const decimals = (match[0].split('.')[1] || '').length;
    const formatted = new Intl.NumberFormat('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(
      decimals ? value : Math.ceil(value)
    );
    return template.replace(NUMBER_PATTERN, formatted);
  }

  render() {
    const rate = Number(/** @type {any} */ (window).Shopify?.currency?.rate) || 1;
    const total = Number(this.dataset.total) || 0;
    const tiers = (this.dataset.tiers || '')
      .split(',')
      .map((amount) => Math.round(Number(amount) * 100 * rate))
      .filter((amount) => amount > 0);
    /** @type {string[]} */
    let labels = [];
    try {
      labels = JSON.parse(this.dataset.labels || '[]');
    } catch {
      labels = [];
    }
    const message = this.querySelector('[data-mc-cart-progress-message]');
    const track = this.querySelector('.mc-cart-progress__track');
    const fill = /** @type {HTMLElement | null} */ (this.querySelector('.mc-cart-progress__fill'));
    const goal = this.querySelector('.mc-cart-progress__goal');
    const markers = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.mc-cart-progress__marker')));
    if (!tiers.length || !message || !track || !fill) return;

    const top = tiers[tiers.length - 1];
    const nextIndex = tiers.findIndex((amount) => total < amount);
    const percent = Math.max(0, Math.min(100, Math.round((total / top) * 100)));

    message.textContent = '';
    if (nextIndex === -1) {
      const icon = document.createElement('span');
      icon.className = 'mc-cart-progress__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML =
        '<svg viewBox="0 0 12 12" width="12" height="12"><path d="M2.5 6.2 5 8.5 9.5 3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      message.append(icon);
    }
    const label = document.createElement('strong');
    label.className = 'mc-cart-progress__tier';
    // Labels name the threshold as [amount] (or a literal £ figure from older settings); either is
    // shown in the shopper's own currency so the bar never mixes £ with their prices.
    const labelIndex = nextIndex === -1 ? labels.length - 1 : nextIndex;
    // Rounded up to a whole unit, so anyone who reaches the shown figure really qualifies.
    const tierMoney = this.#money(Math.ceil((tiers[labelIndex] || 0) / 100) * 100).replace(/\.00(?=\D*$)/, '');
    label.textContent = String(labels[labelIndex] || '')
      .replace('[amount]', tierMoney)
      .replace(/£\s?\d[\d,]*(\.\d+)?/, tierMoney);
    const detail = document.createElement('span');
    detail.className = 'mc-cart-progress__to-go';
    detail.textContent =
      nextIndex === -1
        ? this.dataset.unlocked || ''
        : (this.dataset.toGo || '[amount] to go').replace('[amount]', this.#money(Math.ceil(tiers[nextIndex] / 100) * 100 - total));
    message.append(label, document.createTextNode(' '), detail);

    this.classList.toggle('mc-cart-progress--reached', nextIndex === -1);
    track.setAttribute('aria-valuenow', String(percent));
    track.setAttribute('aria-label', label.textContent || '');
    fill.style.setProperty('--mc-progress', String(percent / 100));
    markers.forEach((marker, index) => {
      const at = tiers[index];
      if (at === undefined) return;
      marker.style.setProperty('--mc-marker', `${(at / top) * 100}%`);
      marker.classList.toggle('mc-cart-progress__marker--reached', total >= at);
    });
    if (goal) goal.textContent = nextIndex === -1 ? '' : this.#money(Math.ceil(tiers[nextIndex] / 100) * 100).replace(/\.00(?=\D*$)/, '');
  }
}

if (!customElements.get('mc-cart-progress')) customElements.define('mc-cart-progress', McCartProgress);

/* ---------------------------------------------------------------------------
 * Members: turn Toki points into a discount code for this order, inside the cart.
 * Contract (verified for Toki): tokiWallet.toki_generate_discount({ shop, tokenAmount,
 * storeCreditId }) resolves { code }; the code is applied with POST /cart/update.js.
 * ------------------------------------------------------------------------- */

class McCartRedeem extends HTMLElement {
  static get observedAttributes() {
    return ['data-code', 'data-total', 'data-replaced'];
  }

  /** @type {{ points: number, value: number } | null} */
  #balance = null;
  #state = 'idle';
  #busy = false;
  #error = '';

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#loadBalance();
    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    this.#render();
  }

  get strings() {
    try {
      return JSON.parse(this.dataset.strings || '{}');
    } catch {
      return {};
    }
  }

  async #loadBalance() {
    const api = /** @type {any} */ (window).MCToki;
    if (!api || typeof api.getBalance !== 'function') {
      document.addEventListener('mc:toki:balance', () => this.#loadBalance(), { once: true });
      return;
    }
    try {
      const result = await api.getBalance();
      const points = Number(result?.points);
      const value = Number(result?.value);
      this.#balance = points > 0 && value > 0 ? { points, value } : null;
    } catch {
      this.#balance = null;
    }
    this.#render();
  }

  /** @param {number} minor */
  #money(minor) {
    const template = this.dataset.money || '';
    const match = template.match(NUMBER_PATTERN);
    if (!match) return (minor / 100).toFixed(2);
    const decimals = (match[0].split('.')[1] || '').length;
    return template.replace(
      NUMBER_PATTERN,
      new Intl.NumberFormat('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(minor / 100)
    );
  }

  /** Points and value to redeem, capped at the cart total. */
  #plan() {
    if (!this.#balance) return null;
    const { points, value } = this.#balance;
    const rate = points / value;
    const balanceMinor = Math.round(value * 100);
    const totalMinor = Number(this.dataset.total) || 0;
    const useMinor = Math.min(balanceMinor, totalMinor);
    const usePoints = Math.min(points, Math.floor((useMinor * rate) / 100 + 1e-6));
    if (usePoints <= 0) return null;
    return { points: usePoints, valueMinor: Math.round((usePoints / rate) * 100), partial: usePoints < points, balanceMinor };
  }

  #render() {
    const s = this.strings;
    const code = this.dataset.code || '';
    this.replaceChildren();
    this.classList.remove('is-active');

    // Store credit codes are in the shop currency; only offer them on carts in that currency.
    const sameCurrency = !this.dataset.shopCurrency || this.dataset.currency === this.dataset.shopCurrency;
    const line = document.createElement('div');
    line.className = 'mc-club-redeem__line';

    if (code) {
      this.classList.add('is-active');
      const text = document.createElement('p');
      text.className = 'mc-club-redeem__text';
      text.textContent = (s.applied || '').replace('[amount]', this.#money(Number(this.dataset.codeAmount) || 0)).replace('[code]', code);
      line.append(text, this.#button(s.remove || '', 'remove', 'mc-club-redeem__link'));
      this.append(line);
      const meta = document.createElement('p');
      meta.className = 'mc-club-redeem__meta';
      meta.textContent = (s.newTotal || '').replace('[total]', this.#money(Number(this.dataset.total) || 0));
      if (this.dataset.replaced === 'true') meta.textContent = `${s.conflict || ''} ${meta.textContent}`;
      this.append(meta);
      this.#appendError();
      return;
    }

    const plan = sameCurrency ? this.#plan() : null;
    if (!plan) return;
    this.classList.add('is-active');

    const text = document.createElement('p');
    text.className = 'mc-club-redeem__text';
    if (this.#state === 'confirm') {
      text.textContent = (s.confirm || '').replace('[points]', plan.points.toLocaleString('en-GB')).replace('[amount]', this.#money(plan.valueMinor));
      line.append(text);
      const actions = document.createElement('span');
      actions.className = 'mc-club-redeem__actions';
      actions.append(this.#button(s.apply || '', 'confirm', 'mc-club-redeem__apply'), this.#button(s.cancel || '', 'cancel', 'mc-club-redeem__link'));
      line.append(actions);
    } else {
      text.textContent = plan.partial
        ? (s.partial || '').replace('[amount]', this.#money(plan.valueMinor)).replace('[value]', this.#money(plan.balanceMinor))
        : (s.offer || '').replace('[value]', this.#money(plan.balanceMinor));
      line.append(text, this.#button(s.apply || '', 'ask', 'mc-club-redeem__apply'));
    }
    this.append(line);
    this.#appendError();
  }

  #appendError() {
    if (!this.#error) return;
    const error = document.createElement('p');
    error.className = 'mc-club-redeem__error';
    error.setAttribute('role', 'alert');
    error.textContent = this.#error;
    this.append(error);
  }

  /**
   * @param {string} label
   * @param {string} action
   * @param {string} className
   */
  #button(label, action, className) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.dataset.action = action;
    button.textContent = label;
    if (this.#busy) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    return button;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = /** @type {Element} */ (event.target).closest?.('button[data-action]');
    if (!(button instanceof HTMLButtonElement) || this.#busy) return;
    const action = button.dataset.action;
    this.#error = '';
    // Re-render after the click finishes, so the drawer never sees a click on a detached node.
    window.setTimeout(() => this.#handle(action || ''), 0);
  };

  /** @param {string} action */
  #handle(action) {
    if (action === 'ask') {
      this.#state = 'confirm';
      this.#render();
      /** @type {HTMLElement | null} */ (this.querySelector('[data-action="confirm"]'))?.focus();
    } else if (action === 'cancel') {
      this.#state = 'idle';
      this.#render();
      /** @type {HTMLElement | null} */ (this.querySelector('[data-action="ask"]'))?.focus();
    } else if (action === 'confirm') {
      this.#apply();
    } else if (action === 'remove') {
      this.#updateDiscount('');
    }
  }

  async #apply() {
    const plan = this.#plan();
    const wallet = /** @type {any} */ (window).tokiWallet;
    if (!plan || !wallet || typeof wallet.toki_generate_discount !== 'function') {
      this.#error = this.strings.error || '';
      this.#render();
      return;
    }
    this.#busy = true;
    this.#render();
    try {
      const balance = await wallet.getUserBalance();
      const result = await wallet.toki_generate_discount({
        shop: wallet.config?.shopDomain || /** @type {any} */ (window).Shopify?.shop,
        tokenAmount: plan.points,
        storeCreditId: balance?.storeCreditId,
      });
      const code = result?.code;
      if (!code) throw new Error('No code returned');
      this.#state = 'idle';
      await this.#updateDiscount(code, true);
      /** @type {any} */ (window).MCToki?.refresh?.();
    } catch (error) {
      console.error(error);
      this.#busy = false;
      this.#error = this.strings.error || '';
      this.#render();
    }
  }

  /**
   * @param {string} code
   * @param {boolean} [fromApply]
   */
  async #updateDiscount(code, fromApply = false) {
    this.#busy = true;
    if (!fromApply) this.#render();
    try {
      const response = await fetch(`${Theme.routes.cart_update_url}`, {
        ...fetchConfig('json', {
          body: JSON.stringify({ discount: code, sections: cartSectionIds().join(','), sections_url: window.location.pathname }),
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || 'Discount update failed');
      if (!code) this.#state = 'idle';
      this.#busy = false;
      document.dispatchEvent(
        new CartUpdateEvent(data, 'mc-cart-redeem', {
          source: 'mc-cart-redeem',
          itemCount: data.item_count,
          sections: data.sections,
          // @ts-ignore custom flag read by cart-drawer.js
          skipOpen: true,
        })
      );
      this.#render();
    } catch (error) {
      console.error(error);
      this.#busy = false;
      this.#error = this.strings.error || '';
      this.#render();
    }
  }
}

if (!customElements.get('mc-cart-redeem')) customElements.define('mc-cart-redeem', McCartRedeem);
